import React, { useState, useCallback } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { useAccess } from 'contexts/access'
import { useAbsenceEntries } from 'contexts/absence-entries'
import { useMe } from 'contexts/me'
import { useInterval } from 'hooks/interval'
import { usePossum } from 'hooks/possum'
import { useNavigate } from 'react-router-dom'
import { useTypeNameFormatter } from 'pages/absence/components/type-name'
import { isAfter } from 'date-fns'
import { getDate } from 'utilities/date-time'
import paths from 'app/paths'
import { getShortName } from 'utilities/person'
import { getPeopleProfileAbsenceUrl } from 'utilities/url'
import { sendAppSignal } from 'hooks/app-signal'
import { Header, Heading, CloseButton } from './s'
import Badge from 'components/badge'
import Requests from 'lists/absence/requests'
import Modal from 'modals/dialog'
import AddToCalendar from 'components/add-to-calendar'

const AbsenceRequests = ({ dismiss, salt }) => {
    const { formatMessage } = useIntl()

    const { check } = useAccess()
    const absenceAdmin = check('absence:manage')

    const {
        total,
        endEntry,
        removeEntry
    } = useAbsenceEntries()

    const { isItMyOwnId } = useMe()

    const typeNameFormatter = useTypeNameFormatter()

    const [now, setNow] = useState(new Date)
    useInterval(() => setNow(new Date), 60 * 60 * 1000)

    const [addingToCalendar, setAddingToCalendar] = useState(null)

    const possessify = usePossum()

    const navigate = useNavigate()

    const viewAction = useCallback(entry => {
        const { user } = entry

        if(user?.status?.active === false) {
            return null
        }

        return {
            salt: `${salt}:view`,
            label: formatMessage({
                id: 'action_view_details',
                defaultMessage: 'View details'
            }),
            effect: 'neutral',
            onClick: () => sendAppSignal('absence:entry:act', {
                entry,
                mode: 'view',
                provide: ['approve', 'edit'].includes(entry.permissionLevel)
            })
        }
    }, [salt])

    const navigateToUserAbsenceAction = useCallback(({ user }) => {
        if(user?.status?.active === false) {
            return null
        }

        const ownAbsence = isItMyOwnId(user.id)

        return {
            salt: `${salt}:user:absence`,
            label: formatMessage({
                id: ownAbsence ?
                    'absence_action_navigate_me' :
                    'absence_action_navigate_user',
                defaultMessage: ownAbsence ?
                    'Go to your absence' :
                    'Go to {pname} absence'
            }, {
                pname: possessify(getShortName(user))
            }),
            effect: 'neutral',
            element: 'link',
            link: {
                to: ownAbsence ?
                    paths.absence.base :
                    getPeopleProfileAbsenceUrl(user)
            }
        }
    }, [salt])

    const viewAbsenceCalendarAction = useCallback(({ start, end, user, id }) => {
        if(!end || user?.status?.active === false) {
            return null
        }

        return ({
            salt: `${salt}:absence-calendar`,
            label: formatMessage({
                id: 'absence_action_view_in_calendar',
                defaultMessage: 'View in absence calendar'
            }),
            effect: 'neutral',
            onClick: () => navigate(paths.absence.calendar, {
                state: {
                    start,
                    entryFocus: id
                },
                replace: true
            })
        })
    }, [salt])

    const addToCalendarAction = useCallback(({ start, end, type, user }) => {
        if(!end || user?.status?.active === false) {
            return null
        }

        return {
            salt: `${salt}:external-calendar`,
            label: formatMessage({
                id: 'absence_action_add_to_external_calendar',
                defaultMessage: 'Add to external calendar'
            }),
            effect: 'neutral',
            onClick: () => setAddingToCalendar({
                start,
                end,
                title: typeNameFormatter(type)
            })
        }
    }, [salt])

    const editAction = useCallback(entry => {
        const {
            permissionLevel,
            user,
            status
        } = entry

        if(!['edit', 'approve'].includes(permissionLevel) || user?.status?.active === false || status === 'canceled') {
            return null
        }

        return {
            salt: `${salt}:edit`,
            label: formatMessage({
                id: 'action_edit_details',
                defaultMessage: 'Edit details'
            }),
            effect: 'neutral',
            onClick: () => sendAppSignal('absence:entry:act', {
                entry,
                mode: 'edit'
            })
        }
    }, [salt])

    const endNowAction = useCallback(entry => {
        const {
            end,
            start,
            permissionLevel,
            user
        } = entry

        if(!isAfter(now, start) || !!end || user?.status?.active === false) {
            return null
        }

        if(!['edit', 'approve'].includes(permissionLevel)) {
            return null
        }

        return {
            salt: `${salt}:end-now`,
            label: formatMessage({
                id: 'action_end_now',
                defaultMessage: 'End now'
            }),
            onClick: async () => {
                const result = await endEntry(entry)

                if(result.response?.errorCode?.includes('overlapping') && absenceAdmin) {
                    sendAppSignal('absence:entry:act', {
                        entry: {
                            ...entry,
                            end: getDate()
                        },
                        overrideTouched: true,
                        mode: 'edit'
                    })
                }

                return result
            },
            effect: 'neutral'
        }
    }, [salt])

    const removeAction = useCallback(({ permissionLevel, user, id }) => {
        if(!['edit', 'approve'].includes(permissionLevel) || user?.status?.active === false) {
            return null
        }

        return {
            salt: `${salt}:delete`,
            label: formatMessage({
                id: 'action_delete',
                defaultMessage: 'Delete'
            }),
            prompt: {
                question: formatMessage({
                    id: 'absence_delete_prompt_question',
                    defaultMessage: 'Confirm removing this absence'
                }),
                confirmText: formatMessage({
                    id: 'action_delete',
                    defaultMessage: 'Delete'
                })
            },
            effect: 'destructive',
            onClick: async () => {
                const result = await removeEntry(id)
                sendAppSignal('absence:entry:act', null)

                return result
            }
        }
    }, [salt])

    const actions = {
        view: viewAction,
        navigateToUserAbsence: navigateToUserAbsenceAction,
        viewAbsenceCalendar: viewAbsenceCalendarAction,
        addToCalendar: addToCalendarAction,
        edit: editAction,
        endNow: endNowAction,
        remove: removeAction
    }

    return (
        <>
            <Header>
                <Heading className="compact">
                    <FormattedMessage
                        id="absence_heading_requests"
                        defaultMessage="Absence requests" />
                </Heading>
                <Badge number={total} />
                <CloseButton onClick={dismiss} />
            </Header>
            <Requests actions={actions} />
            <Modal
                show={!!addingToCalendar}
                dismiss={() => setAddingToCalendar(null)}
                className="scrollable"
                salt={`${salt}:add-to-calendar`}>
                <AddToCalendar {...addingToCalendar} />
            </Modal>
        </>
    )
}

export default AbsenceRequests
